import { AlertTriangle } from 'lucide-react';

import { MoveButtons } from '@/components/tasks/MoveActions';
import { TaskList, type TaskActions } from '@/components/tasks/TaskList';
import type { TaskSummary } from '@/lib/api/types';
import { addDays } from '@/lib/dates';
import { useT } from '@/lib/i18n';

/**
 * Everything that slipped, pinned above today's work.
 *
 * This is the product's central promise made visible: the API returns overdue
 * tasks in the same payload as today's, and Today renders them first, so
 * opening the app cannot avoid showing what was missed.
 *
 * The four move actions are inline buttons rather than a menu — resolving a
 * slipped task should cost one click, not two.
 */
export function OverdueBand({
  tasks,
  today,
  currentUserId,
  actions,
}: {
  tasks: TaskSummary[];
  today: string;
  currentUserId: string;
  actions: TaskActions;
}) {
  const t = useT();
  if (tasks.length === 0) return null;

  const single = tasks.length === 1;

  return (
    <section
      className="mb-6 rounded-xl border border-overdue-border bg-overdue-subtle p-3.5"
      aria-labelledby="overdue-heading"
    >
      <div className="mb-2 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-overdue" aria-hidden />
        <h2
          id="overdue-heading"
          className="text-[11px] font-semibold uppercase tracking-wider text-overdue"
        >
          {t('today.overdue')}
        </h2>
        <span className="text-[11px] tabular-nums text-overdue opacity-80">
          {tasks.length}
        </span>
      </div>

      <TaskList
        tasks={tasks}
        today={today}
        currentUserId={currentUserId}
        actions={actions}
        overdue
        showOpenSubtasks
      />

      {/* Bulk resolution: with several slipped tasks, moving them one at a time
          is the friction that makes people ignore the band entirely. */}
      {!single ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-2 border-t border-overdue-border pt-2.5">
          <span className="text-xs text-overdue">{t('today.moveAll')}</span>
          <MoveButtons
            defaultDate={addDays(today, 1)}
            onMove={(target, date) => {
              for (const task of tasks) actions.onMove(task.id, target, date);
            }}
          />
        </div>
      ) : null}
    </section>
  );
}
